import { Save, Plus, Loader2, Copy } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useTaxonomyContext } from '../context/TaxonomyContext';

import { CloneTaxonomyForm } from './CloneTaxonomyForm';
import { DownloadTaxonomyButton } from './DownloadTaxonomyButton';
import SyncTaxonomyButton from './SyncTaxonomyButton';
import { TaxonomyFilters } from './TaxonomyFilters';
import { TaxonomySelects } from './TaxonomySelects';
import { TaxonomyTable } from './TaxonomyTable';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useCreateTaxonomyVersion } from '@/hooks/useCreateTaxonomyVersion';
import { useDisplayedTaxonomies } from '@/hooks/useDisplayedItems';
import { useGetAdminTaxonomiesByCategories } from '@/hooks/useGetAdminTaxonomiesByCategory';
import { useGetAdminTaxonomyCategories } from '@/hooks/useGetAdminTaxonomyCategories';
import { useGetAdminTaxonomiesVersions } from '@/hooks/useGetAdminTaxonomyVersions';
import { useGetCustomerById } from '@/hooks/useGetCustomerById';
import { useTaxonomyCategories } from '@/hooks/useTaxonomyCategories';
import { useWarnOnUnsavedChanges } from '@/hooks/useWarnOnUnsavedChanges';
import { TaxonomyGetResponse } from '@/services/TaxonomiesService';

interface FormProps {
  onSuccess: (value: boolean) => void;
}

interface Filters {
  selectedCategory: string;
  group: string | null;
  groupName: string | null;
  property: string | null;
}

interface DialogStateType {
  type: 'add' | 'edit' | 'removeTaxonomyProperty' | null;
  item: TaxonomyGetResponse | null;
}

export function Form({ onSuccess }: FormProps) {
  const [customerId, setCustomerId] = useState(0);

  const [selectedVersion, setSelectedVersion] = useState('');

  const [showCloneTaxonomyForm, setShowCloneTaxonomyForm] = useState(false);

  const [dialogState, setDialogState] = useState<DialogStateType>({
    type: null,
    item: null,
  });

  const [filters, setFilters] = useState<Filters>({
    selectedCategory: '',
    group: null,
    groupName: null,
    property: null,
  });

  const { taxonomiesUpdated, setProperties } = useTaxonomyContext();

  const { versions, versionsLoading } =
    useGetAdminTaxonomiesVersions(customerId);

  const { customerLoading } = useGetCustomerById(customerId);

  const { categories, categoriesLoading } = useGetAdminTaxonomyCategories(
    customerId,
    selectedVersion,
  );

  const { createTaxonomy, isPending } = useCreateTaxonomyVersion(customerId);

  const { taxonomies, isLoading } = useGetAdminTaxonomiesByCategories(
    customerId,
    selectedVersion,
    filters.selectedCategory,
  );

  const { sortedCategories } = useTaxonomyCategories({
    taxonomies,
    taxonomiesUpdated,
    categories,
  });

  const { displayedItems, uniqueValues } = useDisplayedTaxonomies({
    taxonomies,
    taxonomiesUpdated,
    selectedCategory: filters.selectedCategory,
    group: filters.group,
    groupName: filters.groupName,
    property: filters.property,
  });

  const isLoadingAny =
    isLoading || categoriesLoading || versionsLoading || customerLoading;

  const hasUnsavedChanges = useMemo(() => {
    return (
      taxonomiesUpdated.data.length > 0 ||
      taxonomiesUpdated.taxonomiesToRemove.length > 0
    );
  }, [taxonomiesUpdated]);

  useWarnOnUnsavedChanges(hasUnsavedChanges);

  useEffect(() => {
    setProperties(taxonomies);
    if (sortedCategories.length > 0) {
      if (
        !filters.selectedCategory ||
        !sortedCategories.includes(filters.selectedCategory)
      ) {
        if (filters.selectedCategory !== sortedCategories[0]) {
          setFilters((prev) => ({
            ...prev,
            selectedCategory: sortedCategories[0],
          }));
        }
      }
    } else {
      if (filters.selectedCategory !== '') {
        setFilters((prev) => ({ ...prev, selectedCategory: '' }));
      }
    }
  }, [
    sortedCategories,
    filters.selectedCategory,
    setFilters,
    setProperties,
    taxonomies,
  ]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      group: null,
      groupName: null,
      property: null,
    }));
  }, [customerId, selectedVersion]);

  async function handleSaveChanges() {
    try {
      await createTaxonomy(taxonomiesUpdated);
      onSuccess(true);
    } catch (error) {
      toast.error(
        'An error occurred while saving the taxonomy. Please try again.',
      );
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <main className="container">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-4xl font-bold">Taxonomy Management</h1>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => setShowCloneTaxonomyForm(true)}
            >
              <Copy className="h-4 w-4" />
              Clone Taxonomy
            </Button>

            <DownloadTaxonomyButton
              customerId={customerId}
              selectedVersion={selectedVersion}
              displayedItems={displayedItems}
              disabled={hasUnsavedChanges}
            />

            <SyncTaxonomyButton
              selectedVersion={selectedVersion}
              disabled={customerId !== 0}
            />

            <Button
              onClick={handleSaveChanges}
              disabled={!hasUnsavedChanges || isPending}
              className={`flex items-center gap-2 ${hasUnsavedChanges ? 'bg-green-600 hover:bg-green-700' : ''}`}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving Changes...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                  {hasUnsavedChanges && (
                    <Badge variant="secondary" className="ml-1">
                      *
                    </Badge>
                  )}
                </>
              )}
            </Button>
          </div>
        </div>

        <TaxonomySelects
          customerId={customerId}
          setCustomerId={setCustomerId}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          versions={versions}
        />

        {hasUnsavedChanges && (
          <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-yellow-800">
            You have unsaved changes. Please save your changes before leaving
            this page.
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">Taxonomy Properties</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={() => setDialogState({ type: 'add', item: null })}
              disabled={!displayedItems.length}
            >
              <Plus className="mr-2 h-4 w-4" /> Add New Property
            </Button>
          </div>
        </div>

        <TaxonomyFilters
          filters={filters}
          setFilters={setFilters}
          categories={sortedCategories}
          uniqueValues={uniqueValues}
        />

        <TaxonomyTable
          customerId={customerId}
          displayedItems={displayedItems}
          isLoading={isLoadingAny}
          setDialogState={setDialogState}
          dialogState={dialogState}
        />

        <CloneTaxonomyForm
          open={showCloneTaxonomyForm}
          onClose={() => setShowCloneTaxonomyForm(false)}
        />
      </main>
    </div>
  );
}
